/**
 * GestureSystem — wordless drawing communication.
 *
 * Pointer down + drag ≥ DRAG_THRESHOLD px → gesture mode.
 * Pointer down + release without drag → tap (walk to point).
 *
 * The drawn stroke is shown locally as a glowing trail AND broadcast
 * to the peer via Transport so they see it happen on their screen in
 * real time. On release, we try to recognize the shape:
 *
 *   - STRAIGHT LINE (long and flat)  → firefly streaks along the line
 *   - CIRCLE/LOOP (start ≈ end)      → fireflies gather in the center
 *   - default (scribble)             → fades quietly
 *
 * Recognized gestures also request a special Grandma letter.
 */
import { Transport } from './Transport.js';
import { SFX } from './SFX.js';
import { LetterManager } from './LetterManager.js';

const DRAG_THRESHOLD = 20;         // px movement before a drag becomes a gesture
const STROKE_POINT_INTERVAL_MS = 30;
const STROKE_LIFE_MS = 2000;
const GESTURE_LETTER_COOLDOWN_MS = 45_000; // limit letter spam from gestures

export class GestureSystem {
  constructor(scene) {
    this.scene = scene;
    this._localStroke = null;      // { points: [], startX, startY, graphics, lastPointAt }
    this._remoteStrokes = new Map(); // strokeId -> { graphics, points, spawnedAt }
    this._lastGestureLetterAt = 0;
    this._wireTransport();
  }

  _wireTransport() {
    Transport.on('gesture_start', (payload, fromId) => this._onRemoteStart(payload, fromId));
    Transport.on('gesture_point', (payload, fromId) => this._onRemotePoint(payload, fromId));
    Transport.on('gesture_end', (payload, fromId) => this._onRemoteEnd(payload, fromId));
  }

  // ── Local drawing ────────────────────────────────────────────────

  beginStroke(x, y) {
    this._localStroke = {
      id: `s_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      points: [{ x, y, t: Date.now() }],
      startX: x, startY: y,
      graphics: this.scene.add.graphics().setDepth(42),
      lastPointAt: Date.now(),
      isDrag: false,
    };
    Transport.send('gesture_start', {
      id: this._localStroke.id,
      nx: x / this.scene.scale.width,
      ny: y / this.scene.scale.height,
    });
  }

  continueStroke(x, y) {
    const s = this._localStroke;
    if (!s) return false;
    const dist = Math.hypot(x - s.startX, y - s.startY);
    if (!s.isDrag && dist < DRAG_THRESHOLD) return false;
    s.isDrag = true;
    const now = Date.now();
    if (now - s.lastPointAt < STROKE_POINT_INTERVAL_MS) return true;
    s.lastPointAt = now;
    s.points.push({ x, y, t: now });
    this._redrawLocalStroke();
    Transport.send('gesture_point', {
      id: s.id,
      nx: x / this.scene.scale.width,
      ny: y / this.scene.scale.height,
    });
    return true;
  }

  endStroke() {
    const s = this._localStroke;
    if (!s) return { wasGesture: false };
    this._localStroke = null;
    if (!s.isDrag || s.points.length < 4) {
      // Was just a tap — clean up silently
      s.graphics.destroy();
      return { wasGesture: false };
    }
    Transport.send('gesture_end', { id: s.id });
    const shape = this._recognize(s.points);
    this._fadeStroke(s.graphics);
    this._applyShapeEffect(shape, s.points);
    return { wasGesture: true, shape };
  }

  _redrawLocalStroke() {
    const s = this._localStroke;
    if (!s) return;
    s.graphics.clear();
    // Glow under-layer
    s.graphics.lineStyle(8, 0xfff4a0, 0.25);
    s.graphics.beginPath();
    s.graphics.moveTo(s.points[0].x, s.points[0].y);
    for (let i = 1; i < s.points.length; i++) s.graphics.lineTo(s.points[i].x, s.points[i].y);
    s.graphics.strokePath();
    // Bright line
    s.graphics.lineStyle(2, 0xfff8d4, 0.95);
    s.graphics.beginPath();
    s.graphics.moveTo(s.points[0].x, s.points[0].y);
    for (let i = 1; i < s.points.length; i++) s.graphics.lineTo(s.points[i].x, s.points[i].y);
    s.graphics.strokePath();
  }

  _fadeStroke(graphics) {
    this.scene.tweens.add({
      targets: graphics, alpha: 0, duration: STROKE_LIFE_MS,
      onComplete: () => graphics.destroy(),
    });
  }

  // ── Remote stroke handlers ───────────────────────────────────────

  _onRemoteStart(payload, fromId) {
    const w = this.scene.scale.width;
    const h = this.scene.scale.height;
    const stroke = {
      points: [{ x: payload.nx * w, y: payload.ny * h }],
      graphics: this.scene.add.graphics().setDepth(42),
      spawnedAt: Date.now(),
    };
    this._remoteStrokes.set(payload.id, stroke);
  }

  _onRemotePoint(payload, fromId) {
    const stroke = this._remoteStrokes.get(payload.id);
    if (!stroke) return;
    const w = this.scene.scale.width;
    const h = this.scene.scale.height;
    stroke.points.push({ x: payload.nx * w, y: payload.ny * h });
    // Redraw remote stroke in pink (sister's color)
    stroke.graphics.clear();
    stroke.graphics.lineStyle(8, 0xff80ab, 0.22);
    stroke.graphics.beginPath();
    stroke.graphics.moveTo(stroke.points[0].x, stroke.points[0].y);
    for (let i = 1; i < stroke.points.length; i++) stroke.graphics.lineTo(stroke.points[i].x, stroke.points[i].y);
    stroke.graphics.strokePath();
    stroke.graphics.lineStyle(2, 0xffc8d8, 0.95);
    stroke.graphics.beginPath();
    stroke.graphics.moveTo(stroke.points[0].x, stroke.points[0].y);
    for (let i = 1; i < stroke.points.length; i++) stroke.graphics.lineTo(stroke.points[i].x, stroke.points[i].y);
    stroke.graphics.strokePath();
  }

  _onRemoteEnd(payload, fromId) {
    const stroke = this._remoteStrokes.get(payload.id);
    if (!stroke) return;
    // Fade + remove
    this.scene.tweens.add({
      targets: stroke.graphics, alpha: 0, duration: STROKE_LIFE_MS,
      onComplete: () => {
        stroke.graphics.destroy();
        this._remoteStrokes.delete(payload.id);
      },
    });
    // Recognize peer's shape too and apply visual effects
    const shape = this._recognize(stroke.points);
    this._applyShapeEffect(shape, stroke.points);
  }

  // ── Shape recognition ────────────────────────────────────────────

  _recognize(points) {
    if (!points || points.length < 4) return 'scribble';
    const first = points[0];
    const last = points[points.length - 1];
    const totalDist = Math.hypot(last.x - first.x, last.y - first.y);

    // Bounding box
    let minX = points[0].x, maxX = points[0].x;
    let minY = points[0].y, maxY = points[0].y;
    for (const p of points) {
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    }
    const bw = maxX - minX;
    const bh = maxY - minY;
    const boxDiag = Math.hypot(bw, bh);

    // Path length
    let pathLen = 0;
    for (let i = 1; i < points.length; i++) {
      pathLen += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
    }

    // CIRCLE: start and end are close, path is long, bounding box roughly square
    const closedRatio = totalDist / Math.max(1, boxDiag);
    const aspect = bw / Math.max(1, bh);
    if (closedRatio < 0.3 && pathLen > boxDiag * 1.6 && aspect > 0.55 && aspect < 1.8 && boxDiag > 60) {
      return 'circle';
    }
    // LINE: start-end distance ≈ path length (low curvature), long enough
    if (totalDist > 80 && pathLen < totalDist * 1.25) {
      return 'line';
    }
    return 'scribble';
  }

  _applyShapeEffect(shape, points) {
    if (shape === 'scribble' || !points || points.length === 0) return;
    const scene = this.scene;
    SFX.tap();

    if (shape === 'line') {
      const first = points[0];
      const last = points[points.length - 1];
      const fly = scene.add.circle(first.x, first.y, 3, 0xfff4a0, 1).setDepth(44);
      const halo = scene.add.circle(first.x, first.y, 8, 0xfff4a0, 0.35).setDepth(43);
      scene.tweens.add({
        targets: [fly, halo], x: last.x, y: last.y,
        duration: 900, ease: 'Sine.easeInOut',
        onComplete: () => {
          scene.tweens.add({
            targets: [fly, halo], alpha: 0, duration: 400,
            onComplete: () => { fly.destroy(); halo.destroy(); },
          });
        },
      });
      this._tryRequestLetter('line');
    } else if (shape === 'circle') {
      // Compute centroid
      let cx = 0, cy = 0;
      for (const p of points) { cx += p.x; cy += p.y; }
      cx /= points.length; cy /= points.length;
      // Spawn a cluster of fireflies that rise out of the circle
      for (let i = 0; i < 14; i++) {
        const angle = Math.random() * Math.PI * 2;
        const r = Math.random() * 30;
        const sx = cx + Math.cos(angle) * r;
        const sy = cy + Math.sin(angle) * r;
        const f = scene.add.circle(sx, sy, 1.5 + Math.random(), 0xfff4a0, 0.9).setDepth(44);
        const h = scene.add.circle(sx, sy, 4 + Math.random() * 2, 0xfff4a0, 0.3).setDepth(43);
        scene.tweens.add({
          targets: [f, h],
          y: sy - 60 - Math.random() * 80,
          x: sx + (Math.random() - 0.5) * 40,
          alpha: 0,
          duration: 2200 + Math.random() * 1000,
          ease: 'Sine.easeOut',
          onComplete: () => { f.destroy(); h.destroy(); },
        });
      }
      this._tryRequestLetter('circle');
    }
  }

  _tryRequestLetter(shape) {
    const now = Date.now();
    if (now - this._lastGestureLetterAt < GESTURE_LETTER_COOLDOWN_MS) return;
    this._lastGestureLetterAt = now;
    LetterManager.requestLetter({
      event: 'drew_gesture',
      drewGesture: shape,
      plantsSoFar: [...this.scene._plantsTendedThisSession],
      secondsInGarden: Math.floor((Date.now() - this.scene._sessionStart) / 1000),
    });
  }

  cleanup() {
    if (this._localStroke) { try { this._localStroke.graphics.destroy(); } catch (_) {} this._localStroke = null; }
    for (const s of this._remoteStrokes.values()) { try { s.graphics.destroy(); } catch (_) {} }
    this._remoteStrokes.clear();
  }
}
